import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import { Loader2, Eye, EyeOff, Mail, Lock, User as UserIcon, ShieldCheck, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading, setUser } = useAuth();

  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [showPw, setShowPw] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resumeToken, setResumeToken] = useState(null);
  const [recoveredOffer, setRecoveredOffer] = useState(null);

  const next = params.get("next") || "";
  const recoverParam = params.get("recover");

  useEffect(() => {
    if (!recoverParam) return;
    setMode("register");
    setShowEmailForm(true);
    (async () => {
      try {
        const { data } = await api.get(`/auth/recover/${recoverParam}`);
        setForm((f) => ({ ...f, email: data.email || f.email, name: data.name || f.name }));
        setResumeToken(recoverParam);
        setRecoveredOffer({ discount_code: data.discount_code });
      } catch (e) { /* invalid token */ }
    })();
  }, [recoverParam]);

  useEffect(() => {
    if (mode !== "register") return;
    const email = (form.email || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/auth/signup-progress", {
          email, name: form.name || null, source: "register_form",
        });
        if (data?.resume_token && !resumeToken) setResumeToken(data.resume_token);
      } catch (_e) { /* fail silently */ }
    }, 1200);
    return () => clearTimeout(handle);
  }, [form.email, form.name, mode, resumeToken]);

  useEffect(() => {
    if (!loading && user) {
      const target = next || (user.is_admin ? "/admin" : "/dashboard");
      navigate(target, { replace: true });
    }
  }, [loading, user, navigate, next]);

  useEffect(() => {
    if (params.get("error")) toast.error("Sign-in failed. Please try again.");
  }, [params]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register"
        ? { email: form.email, password: form.password, name: form.name,
            resume_token: resumeToken || undefined,
            // iter151 Slice C — CAN-SPAM: default UNCHECKED. Only true
            // when the customer explicitly ticks the marketing box.
            email_marketing_opt_in: !!form.email_marketing_opt_in }
        : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      if (data?.user) setUser(data.user);
      toast.success(mode === "register" ? "Welcome to JDSN!" : "Signed in");
      const target = next || (data?.user?.is_admin ? "/admin" : "/dashboard");
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const signInWithGoogle = async () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect_uri = window.location.origin + "/auth/google";
    const next_uri = next || "/dashboard";
    sessionStorage.setItem("google_oauth_redirect_uri", redirect_uri);
    sessionStorage.setItem("google_oauth_next", next_uri);

    // iter97 — Multi-tier fallback so Google sign-in works even on Safari
    // networks that block XHR/fetch or drop CORS preflights:
    //   1. axios (fast, session-friendly)
    //   2. native fetch (cache:no-store, bypasses stale SW)
    //   3. window.location.href to /api/auth/google/go which returns a 302
    //      directly to Google — no CORS preflight, no XHR error surface.
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const qs = new URLSearchParams({ redirect_uri, next: next_uri }).toString();
    const goHref = `${backend}/api/auth/google/go?${qs}`;
    let attemptError = null;

    // Attempt 1 — axios
    try {
      const { data } = await api.get("/auth/google/start", {
        params: { redirect_uri, next: next_uri },
        timeout: 8000,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      attemptError = new Error("empty_url_from_backend");
    } catch (e) {
      attemptError = e;
      console.warn("[google-signin] axios failed:", e?.message || e);
    }

    // Attempt 2 — native fetch
    try {
      const r = await fetch(`${backend}/api/auth/google/start?${qs}`, {
        credentials: "include",
        cache: "no-store",
        headers: { "Accept": "application/json" },
      });
      if (r.ok) {
        const data = await r.json();
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      }
      throw new Error(`fetch_http_${r.status}`);
    } catch (e) {
      console.warn("[google-signin] fetch failed, trying window.location fallback:", e?.message || e);
    }

    // Attempt 3 — full-page navigation to /api/auth/google/go (server-side
    // 302 to Google). No XHR, no CORS. Works from any Safari network state.
    // Tiny visual hint so users know something's happening before the tab
    // navigates away.
    toast("Opening Google sign-in…", { duration: 2500 });
    setTimeout(() => {
      window.location.href = goHref;
    }, 100);

    // If the browser never actually navigates (extreme case), surface the
    // real underlying error after a beat so the user can screenshot for
    // support instead of seeing generic "Load failed".
    setTimeout(() => {
      setGoogleLoading(false);
      if (document.visibilityState === "visible") {
        console.error("[google-signin] all attempts failed:", attemptError);
        toast.error(attemptError?.message
          ? `Google sign-in error: ${attemptError.message}. Try email login below.`
          : "Google sign-in failed. Please try email login below.");
      }
    }, 4000);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-5">
          <Logo size={64} />
        </div>

        <div className="bg-white rounded-3xl border border-gray-200 shadow-[0_20px_50px_rgba(6,40,24,0.08)] overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-8 pb-3 text-center">
            <h1 className="text-3xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
              {mode === "register" ? "Create your account" : "Welcome back"}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Sign in with Google for faster booking and account management.
            </p>
          </div>

          {/* Mode tabs */}
          <div className="mx-8 mt-4 grid grid-cols-2 bg-gray-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`h-9 text-sm font-bold rounded-full transition ${mode === "login" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
              data-testid="login-tab-signin"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`h-9 text-sm font-bold rounded-full transition ${mode === "register" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
              data-testid="login-tab-register"
            >
              Create account
            </button>
          </div>

          {/* Welcome discount badge — visible when arriving via a recovery link */}
          {recoveredOffer?.discount_code && mode === "register" && (
            <div className="mx-8 mt-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-700 text-white px-4 py-3 flex items-center gap-3" data-testid="recover-discount-banner">
              <Sparkles className="w-5 h-5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-100">Welcome back</div>
                <div className="text-sm font-extrabold tracking-tight font-[Outfit]">
                  Promo code <span className="font-mono">{recoveredOffer.discount_code}</span> — 10% OFF auto-applies
                </div>
              </div>
            </div>
          )}

          {/* === PRIMARY: Google === */}
          <div className="px-8 pt-5 pb-1">
            <button
              type="button"
              onClick={signInWithGoogle}
              disabled={googleLoading}
              className="relative w-full h-14 rounded-2xl bg-white hover:bg-gray-50 text-gray-900 font-extrabold border-2 border-gray-900 flex items-center justify-center gap-3 disabled:opacity-50 active:scale-[0.99] transition shadow-[0_8px_20px_rgba(6,40,24,0.10)]"
              data-testid="google-signin-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.4-.1-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.6 39.6 16.3 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C41.8 35 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              )}
              Continue with Google
              <span className="absolute -top-2.5 right-3 px-1.5 py-0.5 rounded-full bg-[#047857] text-white text-[9px] uppercase tracking-[0.16em] font-extrabold flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> Recommended
              </span>
            </button>
            <p className="mt-2 text-[11px] text-gray-500 text-center leading-snug">
              One click. We auto-fill your name, email{mode === "register" ? "" : ","} and link past bookings.
            </p>
          </div>

          {/* === SECONDARY: email/password (collapsible) === */}
          <div className="px-8 pb-7 pt-5">
            <button
              type="button"
              onClick={() => setShowEmailForm((v) => !v)}
              className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-500 font-bold hover:text-gray-900 transition"
              data-testid="toggle-email-form"
            >
              <div className="h-px bg-gray-200 flex-1" />
              <span className="px-2">{showEmailForm ? "Hide email" : `Or use email & password`}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showEmailForm ? "rotate-180" : ""}`} />
              <div className="h-px bg-gray-200 flex-1" />
            </button>

            {showEmailForm && (
              <form onSubmit={submit} className="mt-4 space-y-4" data-testid="login-form">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Name</Label>
                    <div className="mt-1.5 relative">
                      <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Your name" className="h-12 pl-10 rounded-xl" required data-testid="login-name" />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Email</Label>
                  <div className="mt-1.5 relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@email.com" className="h-12 pl-10 rounded-xl" required data-testid="login-email" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Password</Label>
                  <div className="mt-1.5 relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="password" type={showPw ? "text" : "password"} autoComplete={mode === "register" ? "new-password" : "current-password"} value={form.password} onChange={(e) => setField("password", e.target.value)} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} className="h-12 pl-10 pr-11 rounded-xl" minLength={mode === "register" ? 8 : undefined} required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-700" aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* iter151 Slice C — CAN-SPAM marketing opt-in. UNCHECKED
                    by default (regulatory requirement). Customers can
                    toggle later from Account Settings. */}
                {mode === "register" && (
                  <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="register-marketing-consent-label">
                    <input
                      type="checkbox"
                      checked={!!form.email_marketing_opt_in}
                      onChange={(e) => setField("email_marketing_opt_in", e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#047857]"
                      data-testid="register-marketing-consent"
                    />
                    <span className="text-[11px] leading-snug text-gray-600">
                      I agree to receive promotional emails from JDSN Lawn Care.
                      You can unsubscribe at any time. See our{" "}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                         className="text-[#047857] font-bold underline">Privacy Policy</a>.
                    </span>
                  </label>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-[#062818] hover:bg-black text-white font-bold text-sm"
                  data-testid="login-submit-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {mode === "register" ? "Create account with email" : "Sign in with email"}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Trust + guest CTA */}
        <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-[#047857]" />
          <span>Encrypted. We never share your details.</span>
        </div>
        <div className="mt-2 text-xs text-gray-500 text-center">
          Prefer not to sign up?{" "}
          <Link to="/book" className="font-bold text-[#047857] hover:underline" data-testid="login-guest-link">
            Book as guest
          </Link>
        </div>
      </div>
    </div>
  );
}
